import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Drawer from '@material-ui/core/Drawer';
import { withStyles } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom';
import { withRouter } from 'react-router';
import Link from '@material-ui/core/Link';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import Avatar from '@material-ui/core/Avatar';
import Divider from '@material-ui/core/Divider';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { deleteWorkspace } from 'redux/modules/workspaces';
import { getCurrentPlanId, convertPlanIdIntoSubscription } from '../../utils/subscription';
import ExpandMore from '@material-ui/icons/ExpandMore';
import {
	AccountIcon,
	CupIcon,
	UsersIcon,
	MoneyBoxIcon,
	FolderIcon,
	FontsIcon,
	SmartFieldsIcon,
	CustomizeIcon,
	DownloadIcon
} from 'theme/icons';
import Typography from '@material-ui/core/Typography';
import { topBarHeight, mainNavWidth } from 'theme/uiConfig';
import {
	ROOT,
	CLOUD_FOLDER,
	SUBSCRIPTION,
	USERS,
	FONTS,
	SMART_FIELDS,
	CUSTOMIZE,
	XC_PLUS,
	ACCOUNT_DETAILS,
	getRoute
} from 'routes';
import { TeamMembersStep } from 'containers/OnBoarding/Steps';
import { USER_ROLES, SUBSCRIPTION_PLAN, getCloudPlans } from '../../config';
import i18n from 'i18n/i18n';
import _ from 'lodash';
import XaraLogo from 'components/XaraLogo/XaraLogo';
import classNames from 'classnames';

const localize = i18n.loc.localize;

const styles = theme => ({
	drawer: {
		width: mainNavWidth,
		flexShrink: 0
	},
	drawerPaper: {
		width: mainNavWidth,
		height: `calc(100% - ${topBarHeight}px)`,
		top: topBarHeight,
		padding: '0 15px'
	},
	userInfo: {
		display: 'flex',
		position: 'relative',
		alignItems: 'center',
		margin: '30px 0 15px',
		paddingBottom: '25px',
		color: theme.palette.colors.colorWithAlpha(theme.palette.colors.zeta, 0.75),
		userSelect: 'none',
		'&.isOpened': {
			'& .userProfileDropdown': {
				'visibility': 'visible',
				'opacity': 1
			},
			'& .expandUserProfile': {
				transform: 'rotate(180deg)'
			}
		},
		'& .avatar': {
			color: theme.palette.colors.delta
		},
		'&:hover': {
			cursor: 'pointer',
			color: '#2888C3',
			'& .avatar': {
				color: '#2888C3'
			}
		}
	},
	userName: {
		fontFamily: '"Open Sans Xara"',
		fontWeight: 'bold',
		fontSize: '16px',
		lineHeight: '22px',
		color: 'inherit',
		paddingRight: '23px'
	},
	userSubtitle: {
		opacity: 1,
		color: 'inherit'
	},
	navLink: {
		fontSize: '16px',
		'&$navLinkSelected': {
			fontWeight: 'bold',
			background: 'none'
		},
		'&$navLinkSelected:hover': {
			background: 'none'
		},
		'&:hover': {
			background: 'none',
			fontWeight: 'bold'
		},
		'&:focus': {
			backgroundColor: 'transparent',
			fontWeight: 'bold'
		}
	},
	navLinkIcon: {
		width: 40,
		marginRight: 15
	},
	navLinkSelected: {},
	avatar: {
		marginRight: 5,
		width: 50,
		height: 50,
		background: 'none',
		transform: 'scale(0.8)',
		color: 'inherit'
	},
	userNameAndRole: {
		position: 'relative',
		width: '100%'
	},
	expandUserProfile: {
		'position': 'absolute',
		'top': 0,
		'right': 0,
		transition: 'transform 0.2s'
	},
	userProfileDropdown: {
		transition: 'opacity 0.2s',
		'opacity': 0,
		'visibility': 'hidden',
		position: 'absolute',
		left: 0,
		bottom: 0,
		width: '100%',
		paddingLeft: '50px',
		'& a': {
			color: theme.palette.colors.colorWithAlpha(theme.palette.colors.zeta, 0.75),
			'&:hover': {
				color: '#616161',
				textDecoration: 'none'
			}
		}
	},
	new: {
		'&:after': {
			padding: 2,
			background: theme.palette.colors.alpha,
			color: theme.palette.colors.white,
			content: `"${localize('main-nav-new')}"`,
			display: 'block',
			fontSize: 8,
			lineHeight: '12px',
			fontWeight: 'normal',
			borderRadius: 3,
			position: 'relative',
			top: -8,
			left: 3,
		}

	}
});

const NAV = [
	{
		path: ROOT,
		text: localize('main-nav-team-profile'),
		icon: CupIcon,
		exact: true,
		onlyAdminVisible: false,
		showForTempCompanyOnPlan: [
			SUBSCRIPTION_PLAN.TEAM_PLUS,
			SUBSCRIPTION_PLAN.ESSENTIAL,
			SUBSCRIPTION_PLAN.WEB,
			SUBSCRIPTION_PLAN.DESIGNER,
			SUBSCRIPTION_PLAN.PHOTO
		],
		showForPerpetualOwners: true,
		isVisibleForDesktopTempTeams: true
	},
	{
		path: USERS,
		text: localize('main-nav-users'),
		icon: UsersIcon,
		oId: TeamMembersStep.oId,
		onlyAdminVisible: true,
		showForPerpetualOwners: true,
		showForTempCompanyOnPlan: [
			SUBSCRIPTION_PLAN.TEAM_PLUS,
			SUBSCRIPTION_PLAN.ESSENTIAL,
			SUBSCRIPTION_PLAN.WEB,
			SUBSCRIPTION_PLAN.DESIGNER,
			SUBSCRIPTION_PLAN.PHOTO
		],
		isVisibleForDesktopTempTeams: true
	},
	{
		path: FONTS,
		text: localize('main-nav-fonts'),
		icon: FontsIcon,
		onlyAdminVisible: true,
		hideForDesktop: true,
		showForTempCompanyOnPlan: getCloudPlans()
	},
	{
		path: CLOUD_FOLDER,
		text: localize('main-nav-folders'),
		icon: FolderIcon,
		onlyAdminVisible: true,
		hideForDesktop: true,
		showForTempCompanyOnPlan: getCloudPlans()
	},
	{
		path: SMART_FIELDS,
		text: localize('main-nav-smartfields'),
		icon: SmartFieldsIcon,
		onlyAdminVisible: true,
		hideForDesktop: true,
		showForTempCompanyOnPlan: getCloudPlans()	
	},
	{
		path: CUSTOMIZE,
		text: localize('main-nav-customize'),
		icon: CustomizeIcon,
		onlyAdminVisible: true,
		hideForDesktop: true,
		showForTempCompanyOnPlan: getCloudPlans()	
	},
	{
		path: SUBSCRIPTION,
		text: localize('main-nav-subscription'),
		icon: MoneyBoxIcon,
		onlyAdminVisible: true,
		isVisibleForTempTeams: true
	},
	{
		path: XC_PLUS,
		text: localize('main-nav-downloads'),
		icon: DownloadIcon,
		onlyAdminVisible: false,
		customClassName: 'new',
		isVisibleForTempTeams: true
	}
];

@withRouter
@withStyles(styles)
class MainNav extends Component {

	constructor(props) {
		super(props);
		
		this.state = {
			isUserProfileDropdownOpen: false
		};
	}

	componentDidUpdate(prevProps) {
		if (prevProps.location.pathname !== this.props.location.pathname) {
			this.setState({
				isUserProfileDropdownOpen: false
			});
		}
	}

	render() {
		const { classes, navOpen } = this.props;

		return (
			<Drawer
				variant='persistent'
				anchor='left'
				open={navOpen}
				classes={{
					root: classes.drawer,
					paper: classes.drawerPaper
				}}
			>
				{this.renderContent()}
				<XaraLogo />
			</Drawer>
		);
	}

	handleProfileDropdownClick(isUserProfileDropdownOpen) {
		this.setState({
			isUserProfileDropdownOpen
		});
	}

	renderUserInfo() {
		const { classes, company, user } = this.props;
		const userRole = _.get(USER_ROLES, `${company.userRole}.label`);
		const { isUserProfileDropdownOpen } = this.state;
		let accountDetailsPath = getRoute(ACCOUNT_DETAILS, company.id);
		accountDetailsPath = accountDetailsPath.replace(/\//, '#');

		return (
			<section
				className={classNames({
					[classes.userInfo]: true,
					['isOpened']: isUserProfileDropdownOpen
				})}

				key='user-profile'
				onClick={() => this.handleProfileDropdownClick(!isUserProfileDropdownOpen)}>
				<Avatar
					alt={localize('main-nav-avatar-alt')}
					className={classNames({
						[classes.avatar]: true,
						['avatar']: true
					})}
				>
					<AccountIcon />
				</Avatar>
				<div className={classes.userNameAndRole}>
					<Typography variant='h1' className={classes.userName}>{user.name}</Typography>
					{userRole && <Typography variant='body2' className={classes.userSubtitle}>{userRole}</Typography>}
					<ExpandMore
						className={classNames({
							[classes.expandUserProfile]: true,
							['expandUserProfile']: true
						})}			
					/>
				</div>

				<div
					className={classNames({
						[classes.userProfileDropdown]: true,
						['userProfileDropdown']: true
					})}>
					<Link href={accountDetailsPath}>
						<Typography variant='body2' className={classes.userSubtitle}>
							{localize('main-nav-change-sign-in-details')}
						</Typography>
					</Link>
				</div>
			</section>
		);
	}

	renderContent() {
		return [
			this.renderUserInfo(),
			<Divider key='divider' />,
			this.renderNav()
		];
	}

	renderNav() {
		return (
			<MenuList key='nav' component='nav' disablePadding>
				{NAV.filter(item => item).map(this.renderNavLink, this)}
			</MenuList>
		);
	}

	renderNavLink(navLink, navLinkIndex) {
		const { classes, company, disabled, isAdmin, isTempCompany, currentPlanId, isDesktopApp, isPerpetualOwner } = this.props;
		const activeSubscription = convertPlanIdIntoSubscription(currentPlanId);
		const { plan } = activeSubscription;

		const {
			path,
			text,
			separator,
			exact = false,
			icon: Icon,
			oId,
			onlyAdminVisible,
			customClassName,
			isVisibleForTempTeams,
			isVisibleForDesktopTempTeams,
			hideForDesktop,
			showForTempCompanyOnPlan,
			showForPerpetualOwners
		} = navLink;
		// Prevent rendering the menu item,
		// if this menu item should be shown anly for admins
		// and the current user is not an Admin)
		// OR Route is not available for companies with a temporary name ([P9052-15208] PDF simplified workflow)
		// Always show Team profile and Users and permissions tab for Team/Team+ [P9052-20321]
		if (onlyAdminVisible && !isAdmin
			|| (isDesktopApp && hideForDesktop)
			|| (isTempCompany && isVisibleForTempTeams !== true &&
				!(isVisibleForDesktopTempTeams && isDesktopApp) &&
				!(showForPerpetualOwners && isPerpetualOwner) &&
				(!showForTempCompanyOnPlan || !showForTempCompanyOnPlan.includes(plan)))
		) {
			return;
		}

		const key = `nav-link-${navLinkIndex}`;
		const companyPath = getRoute(path, company.id);
		if (separator) {
			return <hr key={key} className={classes.separator} />;
		}

		return (
			<MenuItem
				key={key}
				component={NavLink}
				to={companyPath}
				button
				disableGutters
				disableRipple
				exact={exact}
				data-tid={`menu-main-nav-${navLinkIndex}`}
				className={classNames({
					[classes.navLink]: true,
					[classes[customClassName]]: !!customClassName
				})}
				activeClassName={!disabled ? classes.navLinkSelected : ''}
				data-oid={oId}
			>
				{Icon ? <Icon key={`${key}-icon`} className={classes.navLinkIcon} /> : null}
				{text}
			</MenuItem>
		);
	}

	get selectedWorkspaceName() {
		const { rawDataById, selectedId } = this.props.workspaces;
		return (rawDataById && rawDataById[selectedId]) ? rawDataById[selectedId].displayName : '';
	}

}

MainNav.propTypes = {
	classes: PropTypes.object,
	location: PropTypes.object,
	history: PropTypes.object,
	navOpen: PropTypes.bool.isRequired,
	deleteWorkspace: PropTypes.func.isRequired,
	company: PropTypes.object.isRequired,
	workspaces: PropTypes.object.isRequired,
	user: PropTypes.object.isRequired,
	disabled: PropTypes.bool,
	isAdmin: PropTypes.bool,
	isTempCompany: PropTypes.bool,
	currentPlanId: PropTypes.string,
	isDesktopApp: PropTypes.bool,
	isPerpetualOwner: PropTypes.bool,
};

const mapStateToProps = state => ({
	company: state.companies.rawDataById[state.companies.selectedId],
	workspaces: state.workspaces,
	user: state.session.user,
	currentPlanId: getCurrentPlanId(state.companies.rawDataById[state.companies.selectedId]),
});

const mapDispatchToProps = dispatch => bindActionCreators({
	deleteWorkspace
}, dispatch);

export default withRouter(connect(
	mapStateToProps,
	mapDispatchToProps
)(MainNav));



// WEBPACK FOOTER //
// ./src/components/MainNav/MainNav.js